import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Download, ArrowLeft, FileText, ShieldCheck, Mail, Send, Share2, Calendar, Star, FileType2, Lock, Sparkles } from "lucide-react";
import { getReport, downloadPdf, downloadDocx, getAccess, getFeedbackToken, api } from "@/lib/api";
import { trackEvent } from "@/lib/metaPixel";
import ShareModal from "@/components/ShareModal";
import BookingModal from "@/components/BookingModal";
import LivingActionPlan from "@/components/summary/LivingActionPlan";
import { DISCLAIMER_TEXT } from "@/lib/constants";

export default function Export() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [access, setAccess] = useState(null);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [consultantOpen, setConsultantOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getReport(id);
        setReport(r);
        if (r?.email && typeof r.email === "string") {
          setEmail(r.email);
        }
      } catch (e) {
        console.error(e);
        toast.error("Report not available.");
      }
    })();
    getAccess(id).then(setAccess).catch(() => setAccess(null));
  }, [id]);

  // Word download is a paid artifact — offered when the report is unlocked
  // (paid brief) or when the paywall isn't active yet.
  const canWord = !!access && (!access.paywall_active || access.paid_brief);
  const canManaged = !!access && access.paid_managed;

  const sendEmail = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to receive the plan.");
      return;
    }
    try {
      setSending(true);
      await api.post(`/assessment/${id}/email`, { recipient_email: email, consent: true });
      try { trackEvent("Lead", { content_name: "BCP Executive Assessment Report", currency: "USD" }); } catch (err) { /* never block the flow on analytics */ }
      toast.success(`Plan sent to ${email}`);
      setEmail("");
      setConsent(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not send email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700">Export</p>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              Your Resilience Readiness Assessment is ready
            </h1>
            <p className="mt-3 text-slate-600 max-w-2xl">
              A consulting-grade PDF, ready to share with your team or a continuity consultant.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={`/summary/${id}`}
              data-testid="back-to-summary-btn"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Summary
            </Link>
          </div>
        </div>

        {/* Living Action Plan (Managed Resilience) or upgrade CTA — above downloads */}
        {canManaged ? (
          <div className="mt-8">
            <LivingActionPlan assessmentId={id} readiness={report?.readiness} />
          </div>
        ) : (
          <div className="mt-8 rounded-xl border border-teal-200 bg-teal-50/50 p-6 sm:p-8" data-testid="managed-upsell">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="max-w-xl">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-teal-600" />
                  <p className="text-[11px] uppercase tracking-[0.14em] text-teal-700">Managed Resilience</p>
                </div>
                <h3 className="mt-2 font-display text-xl font-semibold text-slate-900 tracking-tight">Your Living Action Plan</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  Unlock a living action plan — assign owners, due dates & status, track progress against your score,
                  export to Excel, and get a quarterly re-score with newly surfaced actions. Resilience as an ongoing habit, not a one-off document.
                </p>
              </div>
              <Link
                to="/pricing"
                data-testid="managed-upsell-cta"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors"
              >
                <Lock className="h-4 w-4" /> Upgrade to Managed
              </Link>
            </div>
          </div>
        )}

        {/* Downloads */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="downloads-block">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Download your report</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={async () => {
                try { await downloadPdf(id); }
                catch (e) { console.error(e); toast.error("Could not download PDF. Please try again."); }
              }}
              data-testid="download-pdf-btn"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              <Download className="h-4 w-4" /> Download PDF
            </button>
            {canWord && (
              <button
                type="button"
                onClick={async () => {
                  try { await downloadDocx(id); }
                  catch (e) { console.error(e); toast.error(e?.response?.data?.detail || "Could not download Word. Please try again."); }
                }}
                data-testid="download-docx-btn"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-slate-300 text-slate-800 text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                <FileType2 className="h-4 w-4" /> Download Word
              </button>
            )}
          </div>
        </div>

        {/* Action row */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setConsultantOpen(true)}
            data-testid="book-consultant-export"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors"
          >
            <Calendar className="h-4 w-4" /> Book a workshop
          </button>
          <button
            onClick={() => setShareOpen(true)}
            data-testid="share-export-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <Share2 className="h-4 w-4" /> Share (30 days)
          </button>
          <button
            onClick={async () => {
              try {
                const t = await getFeedbackToken(id);
                navigate(`/feedback/${t}`);
              } catch (e) { console.error(e); }
            }}
            data-testid="submit-testimonial-export"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <Star className="h-4 w-4 text-amber-500" /> Submit testimonial
          </button>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: FileText, t: "Executive cover page", d: "Branded cover with mode and organisation." },
            { icon: ShieldCheck, t: "Sectioned plan", d: "Risks, dependencies, strategies, priorities, oversights." },
            { icon: Download, t: "Page-numbered PDF", d: "Clean A4 layout, ready to print or share." },
          ].map(({ icon: I, t, d }) => (
            <div key={t} className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center">
                <I className="h-4 w-4" />
              </div>
              <h3 className="mt-4 font-display text-base font-semibold text-slate-900">{t}</h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        {/* Email the plan */}
        <div className="mt-10 rounded-xl border border-slate-200 bg-white p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-xl">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-teal-600" />
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Email the plan</p>
              </div>
              <h3 className="mt-2 font-display text-xl font-semibold text-slate-900 tracking-tight">
                {report?.email ? "Send the PDF to your inbox" : "Send a copy to your inbox"}
              </h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                {report?.email ? (
                  <>
                    We'll send the PDF to <strong className="text-slate-900">{report.email}</strong>.
                    You can change the address below if you'd prefer it sent elsewhere.
                  </>
                ) : (
                  <>We will email the PDF directly to you. Useful for sharing with your team or a continuity consultant.</>
                )}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-3 max-w-2xl">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="email-input"
              className="h-11 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600 transition-colors"
            />
            <button
              onClick={sendEmail}
              disabled={sending}
              data-testid="send-email-btn"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
            >
              {sending ? "Sending…" : (
                <>
                  {email ? `Send PDF to ${email.length > 28 ? email.slice(0, 26) + "…" : email}` : "Send PDF"}
                  <Send className="h-4 w-4" />
                </>
              )}
            </button>
          </div>

          <label className="mt-4 flex items-start gap-2.5 text-xs text-slate-600 max-w-2xl cursor-pointer select-none">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="consent-checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            <span>
              I agree to receive my Resilience Readiness Assessment PDF at this address. SmartNote2Self will not
              send marketing emails without further opt-in. You can request deletion of this record at any time.
            </span>
          </label>
        </div>

        {/* Paper preview */}
        <div className="mt-12">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Preview</p>
          <div className="mt-3 mx-auto max-w-4xl bg-white rounded-md shadow-lg border border-slate-200 overflow-hidden">
            <div className="h-2 bg-slate-900" />
            <div className="h-1 w-1/3 bg-teal-600" />
            <div className="px-10 py-10">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                SmartNote2Self · Biz Resilience Scan
              </p>
              <h2 className="mt-4 font-display text-3xl font-semibold text-slate-900 tracking-tight">
                Resilience Readiness Assessment
              </h2>
              <p className="mt-1 text-slate-500">Operational Resilience Readiness Output</p>

              <div className="mt-8 grid grid-cols-2 gap-y-3 text-sm">
                <span className="text-slate-500">Prepared for</span>
                <span className="text-slate-900">{report?.organization || "Confidential"}</span>
                <span className="text-slate-500">Mode</span>
                <span className="text-slate-900">{ { solopreneur: "Solopreneur", sme: "Small / Medium Enterprise", corporate: "Corporate Department" }[report?.mode] || report?.mode || "" }</span>
                <span className="text-slate-500">Generated</span>
                <span className="text-slate-900">{report?.generated_at || ""}</span>
                <span className="text-slate-500">Prepared with</span>
                <span className="text-slate-900">SmartNote2Self · Biz Resilience Scan</span>
              </div>

              <div className="mt-10 border-t border-slate-200 pt-6">
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Excerpt — Executive Summary</p>
                <p className="mt-3 text-slate-700 leading-relaxed">
                  {report?.sections?.executive_summary?.slice(0, 380) || "—"}
                  {report?.sections?.executive_summary?.length > 380 ? "…" : ""}
                </p>
              </div>

              <p className="mt-12 text-[11px] text-slate-400 text-right">Page 1</p>
            </div>
          </div>
        </div>

        <p data-testid="export-disclaimer" className="mt-10 text-xs text-slate-500 max-w-3xl">
          {DISCLAIMER_TEXT}
        </p>
      </main>

      <ShareModal assessmentId={id} open={shareOpen} onClose={() => setShareOpen(false)} />
      <BookingModal assessmentId={id} open={consultantOpen} onClose={() => setConsultantOpen(false)} defaultEmail={email} />

      <Footer />
    </div>
  );
}
